import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Wand2, Sparkles, Coins, Clock, CheckCircle2, BookOpen, ChevronRight, ArrowLeft, Layers, Image as ImageIcon, Tags, PackageCheck, Plus, X, Users, Laugh, GitBranch, ChevronDown } from "lucide-react";

const GENRES = [
  "Fantasy", "Epic / High Fantasy", "Urban Fantasy", "Paranormal", "Romantasy", "Science Fiction",
  "Dystopian", "Post-Apocalyptic", "Thriller", "Mystery / Crime", "Horror", "Romance", "Historical",
  "Literary", "Adventure", "Young Adult", "Children's", "War / Military", "Western", "Comedy",
];
const HORROR_RX = /\b(horror|haunt|ghost|creep|sinister|eerie|terror|terrify|occult|demon|possess|supernatural|lovecraft|gothic|uncanny|nightmare|dread|slasher)/i;
const DREAD_OPTIONS = [
  { v: 0, label: "Off", hint: "no horror" }, { v: 1, label: "Eerie", hint: "quiet unease" },
  { v: 2, label: "Unsettling", hint: "creeping" }, { v: 3, label: "Sinister", hint: "horror" },
  { v: 4, label: "Terrifying", hint: "full fear" },
];
const TONE_TAGS = [
  "Romantic", "Dark", "Gritty", "Gruesome", "Visceral (graphic injury & death)", "Violent",
  "Suspenseful", "Emotional", "Humorous", "Wholesome", "Slow-burn", "Fast-paced", "Steamy",
  "Hopeful", "Bleak", "Whimsical", "Cinematic",
];
const POV_OPTIONS = [
  "First person, past tense", "First person, present tense",
  "Third person limited, past tense", "Third person, dual POV", "Third person omniscient",
];
const SIZES = [
  { v: 150, label: "Novella", hint: "~150 pages" },
  { v: 300, label: "Novel", hint: "~300 pages" },
  { v: 500, label: "Epic", hint: "~500 pages" },
];
const CAST_KINDS = [
  { v: "protagonist", label: "Protagonist", type: "character", role: "Protagonist" },
  { v: "ally", label: "Ally", type: "character", role: "Ally" },
  { v: "villain", label: "Villain", type: "character", role: "Villain" },
  { v: "supporting", label: "Supporting", type: "character", role: "Supporting character" },
  { v: "place", label: "Place / Location", type: "location", role: "" },
];

export default function AutoWriteWizard({ open, onOpenChange, onDone, authorName = "", initialSaga = null }) {
  const [step, setStep] = useState("seed"); // seed | review | writing | finish
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  // finishing touches
  const [coverStyle, setCoverStyle] = useState("");
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverPreview, setCoverPreview] = useState("");
  const [coverApplied, setCoverApplied] = useState(false);
  const [metaBusy, setMetaBusy] = useState(false);
  const [meta, setMeta] = useState(null);
  const [lotBusy, setLotBusy] = useState(false);

  // seed form
  const [title, setTitle] = useState("");
  const [premise, setPremise] = useState("");
  const [mainChar, setMainChar] = useState("");
  const [antagonist, setAntagonist] = useState("");
  const [setting, setSetting] = useState("");
  const [cast, setCast] = useState([]); // [{name, description}]
  const [genre, setGenre] = useState("");
  const [pov, setPov] = useState("");
  const [toneTags, setToneTags] = useState([]);
  const [styleNote, setStyleNote] = useState("");
  useEffect(() => {
    if (!dreadTouched.current && HORROR_RX.test([genre, ...toneTags, styleNote].join(" "))) setDread((d) => Math.max(d, 3));
  }, [genre, toneTags, styleNote]);
  const [pages, setPages] = useState(300);
  const [customPages, setCustomPages] = useState("");
  const [pacing, setPacing] = useState("standard");
  const [intensity, setIntensity] = useState(1);
  const [humour, setHumour] = useState(1);
  const [dread, setDread] = useState(0);
  const dreadTouched = useRef(false);
  const [reviewFirst, setReviewFirst] = useState(true);
  const [autoFinish, setAutoFinish] = useState(false);
  // saga
  const [sagaMode, setSagaMode] = useState("standalone");
  const [sagaId, setSagaId] = useState("");
  const [sagaTitle, setSagaTitle] = useState("");
  const [sagas, setSagas] = useState([]);
  const [contPreview, setContPreview] = useState(null);
  const [contLoading, setContLoading] = useState(false);
  const [contStory, setContStory] = useState("");
  const [showContStory, setShowContStory] = useState(false);
  const isSaga = sagaMode === "existing_saga" && !!sagaId;

  useEffect(() => {
    if (!open) return;
    api.get("/series").then((r) => setSagas(r.data || [])).catch(() => {});
    if (initialSaga?.id) { setSagaMode("existing_saga"); setSagaId(initialSaga.id); }
  }, [open, initialSaga]);

  useEffect(() => {
    if (!open || sagaMode !== "existing_saga" || !sagaId) { setContPreview(null); return; }
    setContLoading(true); setContPreview(null);
    api.get(`/series/${sagaId}/continuation-preview`)
      .then(({ data }) => { setContPreview(data); setContStory(data.story_so_far || ""); })
      .catch(() => setContPreview(null))
      .finally(() => setContLoading(false));
  }, [open, sagaMode, sagaId]);

  const reset = () => {
    setStep("seed"); setBusy(false); setPlan(null); setJob(null);
    setTitle(""); setPremise(""); setMainChar(""); setAntagonist(""); setSetting(""); setCast([]);
    setGenre(""); setPov(""); setToneTags([]); setStyleNote(""); setPages(300); setCustomPages(""); setPacing("standard"); setIntensity(1); setHumour(1); setDread(0); dreadTouched.current = false;
    setReviewFirst(true); setAutoFinish(false); setSagaMode("standalone"); setSagaId(""); setSagaTitle("");
    setContPreview(null); setContLoading(false); setContStory(""); setShowContStory(false);
    setCoverStyle(""); setCoverBusy(false); setCoverPreview(""); setCoverApplied(false);
    setMetaBusy(false); setMeta(null); setLotBusy(false);
  };

  const toggleTag = (t) => setToneTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  const effPages = customPages && Number(customPages) > 0 ? Number(customPages) : pages;
  const addCast = () => setCast((c) => [...c, { name: "", description: "", kind: "supporting" }]);
  const updateCast = (i, patch) => setCast((c) => c.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const removeCast = (i) => setCast((c) => c.filter((_, idx) => idx !== i));

  const poll = useCallback((pid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/projects/${pid}/generate-draft/status`);
        setJob(r.data);
        if (r.data.status === "done" || r.data.status === "error") {
          clearInterval(pollRef.current); pollRef.current = null;
          if (r.data.status === "done") toast.success("Your book is written — read it through and edit away!");
          else toast.error(r.data.error || "Writing stopped early");
        }
      } catch { /* keep polling */ }
    }, 4000);
  }, []);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startWriting = async (pid, wpc) => {
    const r = await api.post(`/projects/${pid}/generate-draft`, { target: "short", words_per_chapter: wpc, premise, auto_finish: autoFinish, cover_style: coverStyle });
    setJob({ status: "running", total: r.data.estimated_chapters, done: 0, current: "Starting…", error: "" });
    setStep("writing");
    poll(pid);
  };

  const runPlan = async () => {
    if (!premise.trim()) { toast.error("Tell me what the book is about"); return; }
    if (sagaMode !== "existing_saga" && !mainChar.trim()) { toast.error("Name your main character"); return; }
    if (sagaMode === "existing_saga" && !sagaId) { toast.error("Pick which saga this book joins"); return; }
    setBusy(true);
    try {
      const body = {
        title, premise, main_character: mainChar, antagonist, setting, genre, pov,
        cast: cast.filter((c) => (c.name || "").trim()).map((c) => {
          const k = CAST_KINDS.find((x) => x.v === c.kind) || CAST_KINDS[3];
          return { name: c.name.trim(), description: (c.description || "").trim(), type: k.type, role: k.role, comic: !!c.comic };
        }),
        tone_tags: toneTags, style_note: styleNote, pages: effPages, pacing, intensity, humour, dread,
        series_mode: sagaMode, series_id: sagaId, saga_title: sagaTitle,
        story_so_far: (sagaMode === "existing_saga" && sagaId) ? contStory.trim() : "",
      };
      const { data } = await api.post("/projects/auto-write/plan", body);
      setPlan(data);
      if (reviewFirst) setStep("review");
      else await startWriting(data.project_id, data.words_per_chapter);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error === "out_of_credits" ? `Not enough credits (need ${d.needed})` : "Couldn't plan the book"));
    } finally { setBusy(false); }
  };

  const editChapter = (id, patch) => {
    setPlan((p) => ({ ...p, chapters: p.chapters.map((c) => c.id === id ? { ...c, ...patch } : c) }));
  };
  const saveChapterEdit = async (c) => {
    try { await api.put(`/chapters/${c.id}`, { title: c.title, summary: c.summary }); } catch { /* silent */ }
  };

  const confirmWrite = async () => {
    setBusy(true);
    try {
      await Promise.all(plan.chapters.map((c) => api.put(`/chapters/${c.id}`, { title: c.title, summary: c.summary }).catch(() => {})));
      await startWriting(plan.project_id, plan.words_per_chapter);
    } catch { toast.error("Couldn't start writing"); } finally { setBusy(false); }
  };

  const discardAndClose = async () => {
    if (plan?.project_id && step === "review") {
      try { await api.delete(`/projects/${plan.project_id}`); } catch { /* ignore */ }
    }
    reset(); onOpenChange(false);
  };

  const goToBook = () => { const pid = plan?.project_id; reset(); onOpenChange(false); onDone?.(pid); };

  const goFinish = () => {
    const seed = [genre, ...toneTags].filter(Boolean).join(", ");
    setCoverStyle((s) => s || (seed ? `${seed}. Cinematic, evocative, one strong focal image.` : ""));
    setStep("finish");
  };

  const generateCover = async () => {
    if (!plan?.project_id) return;
    setCoverBusy(true); setCoverApplied(false);
    try {
      const { data } = await api.post(`/projects/${plan.project_id}/cover/generate`, { style: coverStyle });
      setCoverPreview(data.image);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error === "out_of_credits" ? `Not enough credits (need ${d.needed})` : "Cover generator is busy — try again"));
    } finally { setCoverBusy(false); }
  };
  const applyCover = async () => {
    if (!plan?.project_id || !coverPreview) return;
    try {
      await api.post(`/projects/${plan.project_id}/cover/apply-generated`, { image: coverPreview });
      setCoverApplied(true);
      toast.success("Cover saved to your book");
    } catch { toast.error("Couldn't save the cover"); }
  };
  const generateMeta = async () => {
    if (!plan?.project_id) return;
    setMetaBusy(true);
    try {
      const { data } = await api.post(`/projects/${plan.project_id}/metadata/generate`);
      setMeta(data);
      toast.success("Blurb & keywords ready");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error === "out_of_credits" ? `Not enough credits (need ${d.needed})` : "Couldn't generate the listing"));
    } finally { setMetaBusy(false); }
  };
  const doTheLot = async () => {
    setLotBusy(true);
    try {
      try {
        const { data } = await api.post(`/projects/${plan.project_id}/cover/generate`, { style: coverStyle });
        setCoverPreview(data.image);
        await api.post(`/projects/${plan.project_id}/cover/apply-generated`, { image: data.image });
        setCoverApplied(true);
      } catch (e) {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Cover step failed — you can retry it below");
      }
      await generateMeta();
      toast.success("The lot is done — cover and listing added");
    } finally { setLotBusy(false); }
  };

  const pct = job?.total ? Math.round((job.done / job.total) * 100) : 0;
  const chip = (active) => `text-[11px] rounded-full px-2.5 py-1 border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:text-foreground"}`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { if (step === "writing" || step === "finish") { const refresh = step === "finish"; reset(); onOpenChange(false); if (refresh) onDone?.(); } else discardAndClose(); } else onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="auto-write-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl">
            <Wand2 className="w-5 h-5 text-primary" /> Auto-write my book
          </DialogTitle>
          <DialogDescription>
            {step === "seed" && "Give me the seed of your story and how big it should be — I'll plan and write the whole thing."}
            {step === "review" && "Here's the blueprint. Tweak anything, then I'll write every chapter."}
            {step === "writing" && "Writing your book chapter by chapter…"}
            {step === "finish" && "Your book is written. Add a cover and an Amazon listing to deliver the lot."}
          </DialogDescription>
        </DialogHeader>

        {step === "seed" && (
          <div className="space-y-5 py-1">
            {isSaga && (
              <div className="space-y-3 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="aw-continuation">
                {contLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" /> Reading the previous book to continue it seamlessly…
                  </div>
                ) : contPreview ? (
                  <>
                    <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-primary" />
                      <p className="text-sm font-semibold">Continuing {contPreview.previous_title ? `“${contPreview.previous_title}”` : "the saga"} — this is Book {contPreview.book_number}</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground -mt-1">Everything below is carried over automatically — you don't need to re-enter it. Anything you add in the Cast box is layered on top as new characters.</p>
                    {contPreview.carried_characters?.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-1.5"><Users className="w-3.5 h-3.5 text-primary" /> Returning cast ({contPreview.carried_count} Codex entries carried)</div>
                        <div className="flex flex-wrap gap-1.5" data-testid="aw-cont-cast">
                          {contPreview.carried_characters.map((c, i) => (
                            <span key={i} className="text-[11px] rounded-full bg-primary/10 text-primary px-2.5 py-1 font-medium">{c.name}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {contPreview.open_threads?.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-1.5"><GitBranch className="w-3.5 h-3.5 text-primary" /> Open threads it can pick up</div>
                        <ul className="space-y-1" data-testid="aw-cont-threads">
                          {contPreview.open_threads.slice(0, 6).map((t, i) => (
                            <li key={i} className="text-xs text-foreground/80 flex gap-1.5"><span className="text-primary">•</span><span>{t}</span></li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div>
                      <button type="button" onClick={() => setShowContStory((v) => !v)} data-testid="aw-cont-story-toggle"
                        className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors">
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showContStory ? "rotate-180" : ""}`} /> Story so far (editable canon the AI will honour)
                      </button>
                      {showContStory && (
                        <Textarea value={contStory} onChange={(e) => setContStory(e.target.value)} rows={5}
                          className="mt-2 text-xs" data-testid="aw-cont-story"
                          placeholder="A recap of the previous book…" />
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">This book will continue the saga — the previous book's Codex and recap carry over automatically.</p>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{isSaga ? "What happens in this book?" : "What's the story about?"} <span className="text-primary">*</span></Label>
              <Textarea value={premise} onChange={(e) => setPremise(e.target.value)} rows={3} data-testid="aw-premise"
                placeholder={isSaga ? "e.g. Six months after the crash, the survivors trace the alien signal to its source — and it answers back." : "e.g. A disgraced knight must escort the child empress across a poisoned kingdom to the one person who can cure her — his own exiled brother."} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Main character {isSaga ? <span className="text-muted-foreground font-normal">(carried over — add one only to introduce a new lead)</span> : <span className="text-primary">*</span>}</Label>
                <Input value={mainChar} onChange={(e) => setMainChar(e.target.value)} placeholder={isSaga ? "Leave blank to keep the returning cast" : "e.g. Ser Aldric Vane"} data-testid="aw-main-character" />
              </div>
              <div className="space-y-1.5">
                <Label>Antagonist <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input value={antagonist} onChange={(e) => setAntagonist(e.target.value)} placeholder="e.g. The Ashen Queen" data-testid="aw-antagonist" />
              </div>
              <div className="space-y-1.5">
                <Label>Setting <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input value={setting} onChange={(e) => setSetting(e.target.value)} placeholder="e.g. The kingdom of Mirevale" data-testid="aw-setting" />
              </div>
              <div className="space-y-1.5">
                <Label>Book title <span className="text-muted-foreground font-normal">(optional — I'll suggest one)</span></Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Leave blank for an AI title" data-testid="aw-title" />
              </div>
            </div>

            <div className="space-y-2 rounded-lg border border-border bg-secondary/20 p-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5"><Users className="w-4 h-4 text-primary" /> Cast &amp; places <span className="text-muted-foreground font-normal">(optional — added to your Codex)</span></Label>
                <Button type="button" size="sm" variant="ghost" onClick={addCast} className="h-7 gap-1 text-xs" data-testid="aw-cast-add"><Plus className="w-3.5 h-3.5" /> Add</Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Name your key characters and places, pick what each is, and describe them. Each becomes a Codex entry the AI keeps consistent across every chapter.</p>
              {cast.length === 0 ? (
                <p className="text-[11px] text-muted-foreground italic">{isSaga ? "The returning cast from earlier books is carried over automatically — only add someone NEW to this book here." : <>Nothing added yet — your main character{antagonist ? " and antagonist" : ""} are seeded automatically.</>}</p>
              ) : (
                <div className="space-y-2" data-testid="aw-cast-list">
                  {cast.map((c, i) => (
                    <div key={i} className="flex gap-2 items-start">
                      <div className="flex-1 space-y-1.5">
                        <div className="flex gap-2">
                          <select value={c.kind} onChange={(e) => updateCast(i, { kind: e.target.value })} data-testid={`aw-cast-kind-${i}`}
                            className="h-8 rounded-md border border-input bg-background px-2 text-xs shrink-0">
                            {CAST_KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
                          </select>
                          <Input value={c.name} onChange={(e) => updateCast(i, { name: e.target.value })}
                            placeholder={c.kind === "place" ? "Place name (e.g. The Drowned Market)" : "Name (e.g. Reva the Surgeon)"}
                            className="h-8 text-sm flex-1" data-testid={`aw-cast-name-${i}`} />
                        </div>
                        <Textarea value={c.description} onChange={(e) => updateCast(i, { description: e.target.value })} rows={2}
                          placeholder={c.kind === "place" ? "What is this place? mood, why it matters…" : "Who are they? traits, ties to the plot…"}
                          className="text-xs" data-testid={`aw-cast-desc-${i}`} />
                        {c.kind !== "place" && (
                          <button type="button" onClick={() => updateCast(i, { comic: !c.comic })} data-testid={`aw-cast-comic-${i}`}
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${c.comic ? "bg-fuchsia-500/15 border-fuchsia-500/40 text-fuchsia-400" : "border-border text-muted-foreground hover:border-fuchsia-500/40"}`}>
                            <Laugh className="w-3 h-3" /> {c.comic ? "One of the funny ones" : "Mark as funny"}
                          </button>
                        )}
                      </div>
                      <button type="button" onClick={() => removeCast(i)} className="text-muted-foreground hover:text-destructive mt-1.5 shrink-0" data-testid={`aw-cast-remove-${i}`}><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Genre</Label>
              <select value={genre} onChange={(e) => setGenre(e.target.value)} data-testid="aw-genre"
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                <option value="">Choose a genre…</option>
                {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label>Tone &amp; content <span className="text-muted-foreground font-normal">(pick any that fit)</span></Label>
              <div className="flex flex-wrap gap-1.5" data-testid="aw-tone-tags">
                {TONE_TAGS.map((t) => (
                  <button key={t} type="button" onClick={() => toggleTag(t)} className={chip(toneTags.includes(t))}
                    data-testid={`aw-tone-${t.split(" ")[0].toLowerCase()}`}>{t}</button>
                ))}
              </div>
              <Input value={styleNote} onChange={(e) => setStyleNote(e.target.value)} className="mt-2"
                placeholder="Anything else? e.g. unflinching cinematic gore, wry banter, lyrical prose" data-testid="aw-style-note" />
            </div>

            <div className="space-y-1.5">
              <Label>Point of view <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <div className="flex flex-wrap gap-1.5">
                {POV_OPTIONS.map((o) => (
                  <button key={o} type="button" onClick={() => setPov(pov === o ? "" : o)} className={chip(pov === o)}
                    data-testid={`aw-pov-${o.split(",")[0].replace(/\s+/g, "-").toLowerCase()}`}>{o}</button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>How long?</Label>
              <div className="grid grid-cols-3 gap-2">
                {SIZES.map((s) => (
                  <button key={s.v} type="button" onClick={() => { setPages(s.v); setCustomPages(""); }} data-testid={`aw-size-${s.v}`}
                    className={`rounded-lg border px-2 py-2.5 text-center transition-colors ${effPages === s.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                    <div className="text-sm font-medium">{s.label}</div>
                    <div className="text-[10px] text-muted-foreground">{s.hint}</div>
                  </button>
                ))}
              </div>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">or custom pages</span>
                <Input type="number" min="20" max="500" step="10" value={customPages} onChange={(e) => setCustomPages(e.target.value)}
                  placeholder="e.g. 220" data-testid="aw-custom-pages" className="h-8 text-xs w-32" />
                <span className="text-[10px] text-muted-foreground">max 500</span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Pacing</Label>
              <div className="grid grid-cols-3 gap-2" data-testid="aw-pacing">
                {[{ v: "slow", label: "Slow-burn", hint: "atmosphere & depth" }, { v: "standard", label: "Standard", hint: "balanced" }, { v: "fast", label: "Fast-paced", hint: "propulsive action" }].map((p) => (
                  <button key={p.v} type="button" onClick={() => setPacing(p.v)} data-testid={`aw-pacing-${p.v}`}
                    className={`rounded-lg border px-2 py-2.5 text-center transition-colors ${pacing === p.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                    <div className="text-sm font-medium">{p.label}</div>
                    <div className="text-[10px] text-muted-foreground">{p.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Content intensity <span className="text-muted-foreground font-normal text-xs">(how graphic violence, injury &amp; death get)</span></Label>
              <div className="grid grid-cols-5 gap-2" data-testid="aw-intensity">
                {[{ v: 0, label: "Tame", hint: "off-page" }, { v: 1, label: "Standard", hint: "balanced" }, { v: 2, label: "Gritty", hint: "grounded" }, { v: 3, label: "Unflinching", hint: "visceral" }, { v: 4, label: "Brutal", hint: "full force" }].map((it) => (
                  <button key={it.v} type="button" onClick={() => setIntensity(it.v)} data-testid={`aw-intensity-${it.v}`}
                    className={`rounded-lg border px-1 py-2 text-center transition-colors ${intensity === it.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                    <div className="text-[11px] font-medium leading-tight">{it.label}</div>
                    <div className="text-[9px] text-muted-foreground">{it.hint}</div>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground">Only affects scenes that already involve violence or injury.</p>
            </div>

            <div className="space-y-1.5">
              <Label>Humour &amp; wit <span className="text-muted-foreground font-normal text-xs">(dry wit, banter &amp; levity)</span></Label>
              <div className="grid grid-cols-5 gap-2" data-testid="aw-humour">
                {[{ v: 0, label: "None", hint: "straight" }, { v: 1, label: "Dry", hint: "subtle wit" }, { v: 2, label: "Light", hint: "warm" }, { v: 3, label: "Playful", hint: "banter" }, { v: 4, label: "Comic", hint: "full comedy" }].map((it) => (
                  <button key={it.v} type="button" onClick={() => setHumour(it.v)} data-testid={`aw-humour-${it.v}`}
                    className={`rounded-lg border px-1 py-2 text-center transition-colors ${humour === it.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                    <div className="text-[11px] font-medium leading-tight">{it.label}</div>
                    <div className="text-[9px] text-muted-foreground">{it.hint}</div>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground">Character-driven wit and banter. A comedic tone or genre nudges it up on its own.</p>
            </div>

            <div className="space-y-1.5">
              <Label>Dread <span className="text-muted-foreground font-normal text-xs">(how sinister &amp; scary it feels)</span></Label>
              <div className="grid grid-cols-5 gap-2" data-testid="aw-dread">
                {DREAD_OPTIONS.map((it) => (
                  <button key={it.v} type="button" onClick={() => { dreadTouched.current = true; setDread(it.v); }} data-testid={`aw-dread-${it.v}`}
                    className={`rounded-lg border px-1 py-2 text-center transition-colors ${dread === it.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                    <div className="text-[11px] font-medium leading-tight">{it.label}</div>
                    <div className="text-[9px] text-muted-foreground">{it.hint}</div>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground">Fear craft: withheld threats, wrongness in the ordinary, rising tension. Picking a horror genre jumps this to Sinister (you can still change it).</p>
            </div>

            <div className="space-y-2 rounded-lg border border-border bg-secondary/20 p-3">
              <Label className="flex items-center gap-1.5"><Layers className="w-4 h-4 text-primary" /> Series / saga</Label>
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setSagaMode("standalone")} className={chip(sagaMode === "standalone")} data-testid="aw-saga-standalone">Standalone book</button>
                <button type="button" onClick={() => setSagaMode("new_saga")} className={chip(sagaMode === "new_saga")} data-testid="aw-saga-new">Start a new saga</button>
                {sagas.length > 0 && (
                  <button type="button" onClick={() => setSagaMode("existing_saga")} className={chip(sagaMode === "existing_saga")} data-testid="aw-saga-existing">Add to a saga</button>
                )}
              </div>
              {sagaMode === "new_saga" && (
                <Input value={sagaTitle} onChange={(e) => setSagaTitle(e.target.value)} placeholder="Saga title (e.g. The Mirevale Cycle)" data-testid="aw-saga-title" />
              )}
              {sagaMode === "existing_saga" && (
                <select value={sagaId} onChange={(e) => setSagaId(e.target.value)} data-testid="aw-saga-select"
                  className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm">
                  <option value="">Pick a saga…</option>
                  {sagas.map((s) => <option key={s.id} value={s.id}>{s.title} ({s.books?.length || 0} book{(s.books?.length || 0) === 1 ? "" : "s"})</option>)}
                </select>
              )}
              {sagaMode === "existing_saga" && sagaId && (
                <p className="text-[11px] text-muted-foreground">The next book will inherit this saga's Codex and a recap of the previous book, so it continues seamlessly.</p>
              )}
            </div>

            <label className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5 cursor-pointer">
              <span className="text-sm">Let me review &amp; tweak the blueprint before writing</span>
              <Switch checked={reviewFirst} onCheckedChange={setReviewFirst} data-testid="aw-review-toggle" />
            </label>

            <div className="rounded-lg border border-border px-3 py-2.5 space-y-2">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm">Auto-create cover &amp; blurb when writing finishes <span className="text-muted-foreground">(+11 credits)</span></span>
                <Switch checked={autoFinish} data-testid="aw-autofinish-toggle"
                  onCheckedChange={(v) => { setAutoFinish(v); if (v && !coverStyle) { const seed = [genre, ...toneTags].filter(Boolean).join(", "); setCoverStyle(seed ? `${seed}. Cinematic, evocative, one strong focal image.` : ""); } }} />
              </label>
              {autoFinish && (
                <Textarea value={coverStyle} onChange={(e) => setCoverStyle(e.target.value)} rows={2} className="text-xs"
                  placeholder="Cover vibe / art direction, e.g. moody oil-painting, muted palette, lone figure on a ridge" data-testid="aw-autofinish-cover-style" />
              )}
              {autoFinish && <p className="text-[11px] text-muted-foreground">Even if you close this, the cover and blurb are created automatically once the last chapter is done.</p>}
            </div>

            <Button className="w-full gap-2" onClick={runPlan} disabled={busy} data-testid="aw-plan-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busy ? "Planning your book…" : (reviewFirst ? "Plan my book" : "Plan & write my book")}
            </Button>
          </div>
        )}

        {step === "review" && plan && (
          <div className="space-y-4 py-1">
            <div className="rounded-lg border border-border bg-secondary/20 p-3">
              <p className="text-xs uppercase tracking-widest text-primary font-semibold">Title</p>
              <p className="font-display text-xl font-bold">{plan.title}{plan.series_order ? <span className="ml-2 text-xs align-middle text-muted-foreground">Book {plan.series_order}</span> : null}</p>
              {plan.carried_entities > 0 && <p className="text-[11px] text-muted-foreground mt-1">Inherited {plan.carried_entities} Codex {plan.carried_entities === 1 ? "entry" : "entries"} from the saga.</p>}
            </div>

            <div className="flex items-center gap-4 rounded-lg border border-border bg-secondary/30 px-3 py-2 text-sm" data-testid="aw-estimate">
              <span className="flex items-center gap-1.5 text-muted-foreground"><BookOpen className="w-4 h-4" /> {plan.chapters_to_write} chapters · ~{plan.pages} pages</span>
              <span className="flex items-center gap-1.5 text-muted-foreground"><Clock className="w-4 h-4" /> ~{plan.est_minutes} min</span>
              <span className="ml-auto flex items-center gap-1.5 font-semibold"><Coins className="w-4 h-4" /> {plan.est_credits} credits</span>
            </div>

            <div className="space-y-3 max-h-[42vh] overflow-y-auto pr-1" data-testid="aw-blueprint">
              {plan.acts.map((a) => {
                const chs = plan.chapters.filter((c) => c.act_id === a.id);
                if (!chs.length) return null;
                return (
                  <div key={a.id} className="space-y-2">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-primary">{a.title}</p>
                    {chs.map((c) => (
                      <div key={c.id} className="rounded-lg border border-border p-2.5 space-y-1.5">
                        <Input value={c.title} onChange={(e) => editChapter(c.id, { title: e.target.value })} onBlur={() => saveChapterEdit(c)}
                          className="h-8 text-sm font-medium" data-testid={`aw-ch-title-${c.order}`} />
                        <Textarea value={c.summary} onChange={(e) => editChapter(c.id, { summary: e.target.value })} onBlur={() => saveChapterEdit(c)}
                          rows={2} placeholder="What happens in this chapter…" className="text-xs" data-testid={`aw-ch-summary-${c.order}`} />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={discardAndClose} className="gap-1.5" data-testid="aw-review-cancel"><ArrowLeft className="w-4 h-4" /> Discard</Button>
              <Button className="flex-1 gap-2" onClick={confirmWrite} disabled={busy} data-testid="aw-write-button">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Write my book ({plan.est_credits} credits) <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {step === "writing" && job && (
          <div className="space-y-4 py-2" data-testid="aw-writing">
            <p className="text-sm text-muted-foreground">
              {job.status === "done" ? "All chapters written — open your book and read it through." : `Writing “${job.current}”… you can close this and it'll keep writing in the background.`}
            </p>
            <div className="h-2.5 w-full rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} data-testid="aw-progress-bar" />
            </div>
            <p className="text-xs text-muted-foreground" data-testid="aw-progress-label">{job.done} / {job.total} chapters {job.status === "done" ? "✓" : ""}</p>
            {job.status === "done" ? (
              autoFinish ? (
                <div className="space-y-2">
                  <p className="text-xs text-emerald-500 flex items-center gap-1.5" data-testid="aw-autofinish-done"><CheckCircle2 className="w-4 h-4" /> Cover &amp; blurb added automatically.</p>
                  <Button className="w-full gap-2" onClick={goToBook} data-testid="aw-open-book"><BookOpen className="w-4 h-4" /> Open my book</Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button className="flex-1 gap-2" onClick={goFinish} data-testid="aw-add-finishing">
                    <PackageCheck className="w-4 h-4" /> Add cover &amp; blurb
                  </Button>
                  <Button variant="outline" className="gap-2" onClick={goToBook} data-testid="aw-open-book"><BookOpen className="w-4 h-4" /> Open my book</Button>
                </div>
              )
            ) : (
              <Button variant="outline" className="w-full" onClick={() => { reset(); onOpenChange(false); onDone?.(); }} data-testid="aw-close-running">
                Close — it'll keep writing in the background
              </Button>
            )}
          </div>
        )}

        {step === "finish" && plan && (
          <div className="space-y-4 py-1" data-testid="aw-finish">
            <Button className="w-full gap-2" onClick={doTheLot} disabled={lotBusy || coverBusy || metaBusy} data-testid="aw-do-the-lot">
              {lotBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {lotBusy ? "Doing the lot…" : "Do the lot — cover + blurb (11 credits)"}
            </Button>

            {/* Cover */}
            <div className="rounded-lg border border-border p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold"><ImageIcon className="w-4 h-4 text-primary" /> Cover
                {coverApplied && <span className="ml-auto text-[11px] text-emerald-500 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Saved</span>}
              </div>
              {authorName && <p className="text-[11px] text-muted-foreground">Credited as <b>{authorName}</b>.</p>}
              <Textarea value={coverStyle} onChange={(e) => setCoverStyle(e.target.value)} rows={2} className="text-xs"
                placeholder="Cover vibe / art direction, e.g. moody oil-painting, muted palette, lone figure on a ridge" data-testid="aw-cover-style" />
              {coverPreview && (
                <img src={coverPreview} alt="cover preview" data-testid="aw-cover-preview"
                  className="w-32 rounded-md border border-border mx-auto" />
              )}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={generateCover} disabled={coverBusy || lotBusy} className="gap-1.5 flex-1" data-testid="aw-cover-generate">
                  {coverBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {coverPreview ? "Regenerate (8)" : "Generate cover (8 credits)"}
                </Button>
                {coverPreview && !coverApplied && (
                  <Button size="sm" onClick={applyCover} className="gap-1.5" data-testid="aw-cover-apply"><CheckCircle2 className="w-4 h-4" /> Use this</Button>
                )}
              </div>
            </div>

            {/* Blurb & keywords */}
            <div className="rounded-lg border border-border p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold"><Tags className="w-4 h-4 text-primary" /> Blurb, keywords &amp; categories
                {meta && <span className="ml-auto text-[11px] text-emerald-500 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Saved</span>}
              </div>
              {meta ? (
                <div className="space-y-2" data-testid="aw-meta-result">
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto">{meta.blurb}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(meta.keywords || []).map((k, i) => (
                      <span key={i} className="text-[10px] rounded-full bg-secondary px-2 py-0.5">{k}</span>
                    ))}
                  </div>
                  {(meta.categories || []).map((c, i) => <p key={i} className="text-[11px] text-muted-foreground">📚 {c}</p>)}
                  <Button size="sm" variant="ghost" onClick={generateMeta} disabled={metaBusy} className="gap-1.5 h-7 text-xs" data-testid="aw-meta-regenerate">
                    {metaBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Regenerate
                  </Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={generateMeta} disabled={metaBusy || lotBusy} className="gap-1.5 w-full" data-testid="aw-meta-generate">
                  {metaBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate blurb &amp; keywords (3 credits)
                </Button>
              )}
            </div>

            <Button className="w-full gap-2" onClick={goToBook} data-testid="aw-finish-open-book"><BookOpen className="w-4 h-4" /> Open my book</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
